/**
 * The chat of a meeting: the lines, and — inside the room — the composer.
 *
 * `MeetChatMessages` is the list alone, used both live in the side panel and
 * on the history page afterwards, so the two read the same. Consecutive lines
 * from the same person fold under one name, like the transcript; our own
 * lines read "Tu" (by participant row in the room, by user on the history
 * page, where a colleague has no participant token any more).
 *
 * `MeetChatPanel` adds the composer: Enter sends, Shift+Enter breaks the
 * line, and the list follows the newest line unless you scrolled up to read.
 */
import { Loader2, Send } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import type { MeetChatMessage } from '../../lib/meetApi';
import { chatTimeFormat, groupBySender, isOwnRun } from '../../lib/meetChatFormat';
import { Avatar } from '../tasks/linear/Avatar';

export function MeetChatMessages({
  messages,
  meParticipantId,
  meId,
  emptyText,
}: {
  messages: MeetChatMessage[];
  /** In the room: our own participant row, so our lines read "Tu" even as a guest. */
  meParticipantId?: string;
  /** On the history page: our user. */
  meId?: string;
  emptyText: string;
}) {
  const { t } = useI18n();
  const runs = groupBySender(messages);
  if (runs.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }
  return (
    <ol className="space-y-4">
      {runs.map((run) => {
        const isMe = isOwnRun(run, { participantId: meParticipantId, userId: meId });
        return (
          <li key={run.key}>
            <div className="flex items-center gap-2">
              <Avatar name={run.senderName} size={22} />
              <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                {isMe ? t('meet.you') : run.senderName}
              </span>
              <time
                dateTime={run.lines[0].createdAt}
                className="text-[11px] tabular-nums text-muted-foreground"
              >
                {chatTimeFormat.format(new Date(run.lines[0].createdAt))}
              </time>
            </div>
            <ol className="mt-1 space-y-1">
              {run.lines.map((line) => (
                <li key={line.id}>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90">
                    {line.text}
                  </p>
                </li>
              ))}
            </ol>
          </li>
        );
      })}
    </ol>
  );
}

/** Still counts as "at the live edge" within this many pixels of the bottom. */
const FOLLOW_THRESHOLD_PX = 120;
const MAX_CHARS = 2000;

export function MeetChatPanel({
  messages,
  meParticipantId,
  sending,
  error,
  onSend,
}: {
  messages: MeetChatMessage[];
  meParticipantId: string;
  sending: boolean;
  error: string | null;
  onSend: (text: string) => Promise<boolean>;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const atEdge = useRef(true);

  // Follow the newest line, unless the reader scrolled up on purpose.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || !atEdge.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // One line of text, growing with its content up to a few rows.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [draft]);

  const submit = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    atEdge.current = true;
    if (await onSend(text)) setDraft('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollerRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          atEdge.current = el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_THRESHOLD_PX;
        }}
        className="min-h-0 flex-1 overflow-y-auto px-4 py-3"
      >
        <MeetChatMessages
          messages={messages}
          meParticipantId={meParticipantId}
          emptyText={t('meet.chat.empty')}
        />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="shrink-0 border-t border-border px-3 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] md:pb-2"
      >
        {error && <p className="mb-1.5 text-xs text-red-400">{t(error)}</p>}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_CHARS))}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={t('meet.chat.placeholder')}
            aria-label={t('meet.controls.chat')}
            className="min-h-10 min-w-0 flex-1 resize-none rounded-md border border-border bg-accent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={sending || draft.trim().length === 0}
            title={t('meet.chat.send')}
            aria-label={t('meet.chat.send')}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-600 text-fixed-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </form>
    </div>
  );
}
